import { useState } from 'react';

import { notify } from '../../shared';
import { getErrorMessage } from '../../utilities';
import { useFetch, useRequest } from '../../hooks';
import { Button, SubmitButton } from '../../components';
import { Modal, Form, FormItem, Input } from '../../components';
import {
  getCampaignSecrets,
  getCampaignSecret,
  setCampaignSecret,
  deleteCampaignSecret,
  KEY_NAME_OPENROUTER,
} from '../../api/campaigns';
import type { CampaignSecretResponse } from '../../services';

type CampaignSecretModalProps = {
  campaignId: string;
  onClose: () => void;
};

type SecretForm = {
  value?: string;
  label?: string;
};

const CampaignSecretModal: React.FC<CampaignSecretModalProps> = ({
  campaignId,
  onClose,
}) => {
  const [secret, setSecret] = useState<CampaignSecretResponse | undefined>();
  const [values, setValues] = useState<SecretForm>({
    value: '',
    label: '',
  });

  const [handleGetSecret, refreshing] = useRequest(
    (_signal, keyName: string) => getCampaignSecret(campaignId, keyName),
    {
      deps: [campaignId],
      onSuccess: result => {
        setSecret(result);
        setValues(current => ({
          ...current,
          label: result.label ?? current.label,
        }));
      },
      onError: ex => {
        notify.error(getErrorMessage(ex, 'Failed to load secret details.'));
      },
    }
  );

  const listSecrets = useFetch<Array<CampaignSecretResponse>>(
    async () => getCampaignSecrets(campaignId),
    [campaignId],
    {
      onSuccess: result => {
        const openRouterSecret = result?.find(
          item => item.key_name === KEY_NAME_OPENROUTER
        );

        if (openRouterSecret) {
          handleGetSecret(openRouterSecret.key_name);
          return;
        }

        setSecret(undefined);
        setValues(current => ({
          ...current,
          label: current.label ?? '',
          value: '',
        }));
      },
      onError: ex => {
        notify.error(getErrorMessage(ex, 'Failed to load campaign secrets.'));
      },
    }
  );

  const [handleSetSecret, saving] = useRequest(
    (_signal, payload: { value: string; label?: string }) =>
      setCampaignSecret(campaignId, KEY_NAME_OPENROUTER, payload),
    {
      deps: [campaignId],
      onSuccess: result => {
        notify.success('OpenRouter secret saved successfully.');
        setSecret(result);
        setValues(current => ({
          ...current,
          label: result.label ?? current.label,
        }));
        listSecrets.refresh();
      },
      onError: ex => {
        notify.error(getErrorMessage(ex, 'Failed to save secret.'));
      },
    }
  );

  const [handleDeleteSecret, deleting] = useRequest(
    () => deleteCampaignSecret(campaignId, KEY_NAME_OPENROUTER),
    {
      deps: [campaignId],
      onSuccess: () => {
        notify.success('OpenRouter secret deleted successfully.');
        setSecret(undefined);
        setValues({ value: '', label: '' });
        listSecrets.refresh();
      },
      onError: ex => {
        notify.error(getErrorMessage(ex, 'Failed to delete secret.'));
      },
    }
  );

  const loading = listSecrets.loading || refreshing || saving || deleting;
  const secretLabel = secret?.label || 'Not set';
  const secretStatus = secret?.status || 'missing';
  const secretLast4 = secret?.last4 ? `•••• ${secret.last4}` : 'Not available';

  const handleSave = () => {
    if (!values.value?.trim()) {
      notify.error('Enter the OpenRouter secret value.');
      return;
    }

    handleSetSecret({
      value: values.value.trim(),
      label: values.label,
    });
  };

  const handleDelete = () => {
    if (!secret) return;
    handleDeleteSecret();
  };

  return (
    <Form<SecretForm> initialValue={values} onChange={setValues}>
      <Modal
        open
        size="big"
        title="Manage Campaign Secrets"
        onClose={onClose}
        bottomActions={
          <>
            <SubmitButton type="primary" loading={loading} onClick={handleSave}>
              Save Secret
            </SubmitButton>
            <Button
              type="danger"
              loading={loading}
              disabled={!secret}
              onClick={handleDelete}
            >
              Delete Secret
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </>
        }
      >
        <div className="flex flex-col gap-y-md">
          <div className="grid grid-cols-1 gap-y-sm">
            <div className="text-small font-semibold">
              OpenRouter secret manager
            </div>
            <div className="text-tiny text-text-muted">
              Only metadata is shown. A new value will create or rotate the
              campaign-scoped OpenRouter secret.
            </div>
          </div>

          <div className="grid gap-y-md">
            <div className="grid grid-cols-1 gap-y-sm rounded-sm border border-content p-md">
              <div className="grid grid-cols-2 gap-x-sm text-small font-semibold">
                <span>Key name</span>
                <span className="text-right">Status</span>
              </div>
              <div className="grid grid-cols-2 gap-x-sm py-sm text-small">
                <span>{KEY_NAME_OPENROUTER}</span>
                <span className="text-right">{secretStatus}</span>
              </div>
              <div className="grid grid-cols-2 gap-x-sm text-small text-muted">
                <span>Label</span>
                <span className="text-right">{secretLabel}</span>
              </div>
              <div className="grid grid-cols-2 gap-x-sm text-small text-muted">
                <span>Last four</span>
                <span className="text-right">{secretLast4}</span>
              </div>
            </div>

            <div className="grid gap-y-md rounded-sm border border-content p-md">
              <div className="text-small font-semibold">Secret details</div>
              <FormItem<SecretForm>
                required
                name="value"
                label="OpenRouter secret value"
              >
                <Input
                  type="password"
                  autoComplete="new-password"
                  placeholder="Enter the OpenRouter key"
                />
              </FormItem>
              <FormItem<SecretForm> label="Label" name="label">
                <Input placeholder="Optional label for this secret" />
              </FormItem>
            </div>
          </div>

          <div className="text-tiny text-text-muted">
            {listSecrets.loading
              ? 'Loading existing secrets...'
              : listSecrets.data?.length
                ? 'Existing campaign secrets are listed above. Only the OpenRouter secret can be managed here.'
                : 'No campaign secrets are currently configured.'}
          </div>
        </div>
      </Modal>
    </Form>
  );
};

export default CampaignSecretModal;
